import { useEffect, useMemo, useState } from 'react';
import { boardLink } from '../tasks/utils/board-navigation';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../global/contexts/auth/auth';
import { useTheme } from '../../global/contexts/theme/theme';
import { IconClose, IconLogout, IconMenu, IconMoon, IconSun } from '../../common/icons';
import { avatarTone, initialsOf } from '../../common/libs/initials';
import { visibleMenu } from './menu';

import './styles/sidebar.css';

// The panel's navigation rail. Layout from design/referencias/panel.webp:
// account block on top, entries grouped under quiet headings, the current one
// held with a soft accent fill.

export default function Sidebar() {
    const navigate = useNavigate();
    const { user, can, logout } = useAuth();
    const { resolved, toggle } = useTheme();

    const groups = useMemo(() => visibleMenu(can), [can]);

    // ON A PHONE, A DRAWER — decided 2026-09-17, reversing the earlier «always
    // open» rule.
    //
    // Stacked above the content, the rail pushed every screen a full phone
    // height down: the first thing anybody saw of any section was the menu to
    // reach it. So under 860px a top bar carries the brand and a ☰ button, and
    // the rail slides in over the content. On a wider screen nothing changes:
    // the rail is always there and the bar does not render.
    //
    // CLOSED BY NAVIGATING, without an effect: the drawer is "open at" the
    // location it was opened on, so any route change — a link in it, the back
    // button — closes it on its own.
    const location = useLocation();
    // The rail survives navigation. Keep the exact board URL so returning from
    // another section restores the explicit URL filters, including an empty filter.
    const currentBoard = ['/admin/development', '/admin/development/tareas'].includes(location.pathname)
        ? boardLink('/admin/development/tareas', new URLSearchParams(location.search)) : null;
    const [lastBoard, setLastBoard] = useState(() =>
        boardLink('/admin/development/tareas', new URLSearchParams(location.search)));
    if (currentBoard && currentBoard !== lastBoard) setLastBoard(currentBoard);
    const [openAt, setOpenAt] = useState(null);
    const open = openAt === location.key;
    const close = () => setOpenAt(null);

    // Esc closes it, and the page behind does not scroll while it is open.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') setOpenAt(null); };
        document.addEventListener('keydown', onKey);
        document.documentElement.classList.add('drawer-open');
        return () => {
            document.removeEventListener('keydown', onKey);
            document.documentElement.classList.remove('drawer-open');
        };
    }, [open]);

    // Signing out is POST /auth/logout, not a navigation: the auth_token cookie
    // is httpOnly and only the backend can clear it. It is also the panel's
    // only mutating request today, so it is the one proving the interceptor
    // sends X-CSRF-Token — without it the backend answers 403.
    const handleLogout = async () => {
        await logout();
        navigate('/login?logout=true', { replace: true });
    };

    return (
        <>
        {/* Phones only (CSS): the brand, and the door to the navigation. */}
        <header className="mobile-bar">
            <span className="mobile-bar__brand">Mastermind</span>
            <button
                type="button"
                className="mobile-bar__toggle"
                onClick={() => setOpenAt(open ? null : location.key)}
                aria-expanded={open}
                aria-controls="app-navigation"
                aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            >
                {open ? <IconClose /> : <IconMenu />}
            </button>
        </header>

        {/* Behind the open drawer; a tap on it closes the drawer. */}
        {open && <button type="button" className="sidebar__backdrop" onClick={close} aria-label="Cerrar menú" />}

        <aside id="app-navigation" className={`sidebar${open ? ' sidebar--open' : ''}`}>
            <div className="sidebar__brand">
                <span className="sidebar__brand-company">Trianametría</span>
                <span className="sidebar__brand-product">Mastermind</span>
            </div>

            {/* The account block is the link to your own screen: it is where
                anybody looks for it. */}
            <NavLink to="/admin/profile" className="sidebar__account">
                <span
                    className={`avatar sidebar__avatar avatar--tone-${avatarTone(user?.name || user?.email)}`}
                    aria-hidden="true"
                >
                    {initialsOf(user?.name || user?.email)}
                </span>
                <span className="sidebar__account-text">
                    <span className="sidebar__account-name">{user?.name || 'Sin nombre'}</span>
                    <span className="sidebar__account-email">{user?.email}</span>
                </span>
            </NavLink>

            <nav className="sidebar__nav">
                {groups.map(group => (
                    <div className="sidebar__group" key={group.title}>
                        <span className="sidebar__group-title">{group.title}</span>
                        <ul className="sidebar__list">
                            {/* The icon is read off the item (<item.Icon />) instead of
                                being destructured: this project's eslint has no react
                                plugin, so it does not count JSX as a use and would warn
                                that a destructured `Icon` is unused. */}
                            {group.items.map(item => (
                                <li key={item.path}>
                                    <NavLink
                                        to={item.path === '/admin/development' ? currentBoard || lastBoard : item.path}
                                        className={({ isActive }) => (
                                            `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
                                        )}
                                    >
                                        <item.Icon />
                                        {item.title}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className="sidebar__foot">
                <button
                    type="button"
                    className="sidebar__link sidebar__link--button"
                    onClick={toggle}
                    /* The button says what it will DO, not what is on screen:
                       "modo oscuro" while the panel is light. A control named
                       after the current state reads as a label. */
                    aria-pressed={resolved === 'dark'}
                >
                    {resolved === 'dark' ? <IconSun /> : <IconMoon />}
                    {resolved === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                </button>

                <button type="button" className="sidebar__link sidebar__link--button" onClick={handleLogout}>
                    <IconLogout />
                    Cerrar sesión
                </button>
            </div>
        </aside>
        </>
    );
}
